<template>
    <page-layout :hide-footer="isBriefAccepted">
        <template>
            <template slot="header">
                <step-header
                    :current-step="stepNumber"
                    :title="pageTitle"
                    :total-steps="totalSteps"
                    :is-loading="isLoading"
                    :use-exit-modal="!isBriefAccepted && agreed === true"
                    :is-preview="isPreview"
                    :skip-steps="isBriefAccepted"
                />
            </template>
            <template slot="default">
                <g-loader v-if="isLoading" type="fixed" />
                <div v-else>
                    <div class="margin-bottom-large">
                        <g-typography tag="p" :value="stepOverview" class="break-new-line" />
                    </div>
                    <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                    <step-error v-if="step && step.error" class="margin-bottom-normal" :step="step" />

                    <template v-if="hasCustomContentRights">
                        <div class="margin-bottom-large">
                            <g-typography v-html="$sanitize(customContentRights)" />
                        </div>
                    </template>
                    <template v-else>
                        <div class="margin-bottom-normal">
                            <g-typography tag="p" :value="agreementText" />
                        </div>
                        <ul class="content-rights-list margin-bottom-large">
                            <li v-for="item in standardContentRights" class="margin-bottom-small">{{ item.text }}</li>
                        </ul>
                    </template>
                    <g-row v-if="contentRightsFiles" class="margin-bottom-small">
                        <g-column
                            v-for="file in contentRightsFiles"
                            :key="file.id"
                            :span="24"
                            class="margin-bottom-small"
                        >
                            <g-tag theme="basic">
                                <g-link :href="file.url" target="_blank">
                                    <g-tooltip :title="file.name" placement="topLeft">
                                        <g-typography size="small"> {{ file.name }}</g-typography>
                                    </g-tooltip>
                                    <g-icon name="paperclip" size="small" />
                                </g-link>
                            </g-tag>
                        </g-column>
                    </g-row>
                    <g-form>
                        <g-form-item :required="true" label="">
                            <g-media-object>
                                <template slot="left">
                                    <g-checkbox
                                        v-if="!isBriefEditable"
                                        :checked="briefAgreed"
                                        class="agreement-checkbox"
                                        :disabled="true"
                                    />
                                    <g-checkbox
                                        v-else
                                        v-model="agreed"
                                        class="agreement-checkbox"
                                        :disabled="!isBriefEditable"
                                    />
                                </template>
                                <template>
                                    <float-on-mask :float-up="isBriefAccepted">
                                        <g-typography v-if="!usesAlternateTerms">
                                            I accept the terms of the foregoing proposal and agree to the
                                            <a href="https://grin.co/privacy/" target="_blank">privacy policy</a>
                                            and
                                            <a href="https://grin.co/terms-of-use-influencers/" target="_blank"
                                                >terms</a
                                            >
                                        </g-typography>
                                        <g-typography v-else>
                                            I accept the above terms and conditions with {{ briefName }} and agree to
                                            the
                                            <a href="https://grin.co/privacy/" target="_blank">privacy policy</a> and
                                            <a href="https://grin.co/terms-of-use-influencers/" target="_blank"
                                                >terms</a
                                            >
                                            provided by {{ briefName }}'s service provider, GRIN.
                                        </g-typography>
                                    </float-on-mask>
                                </template>
                            </g-media-object>
                        </g-form-item>
                    </g-form>
                </div>
            </template>
            <template slot="footer">
                <step-footer
                    v-if="isBriefEditable"
                    :is-next-disabled="isDisabled"
                    :loading="isCompletingBrief"
                    :step-completed="isBriefCompleted"
                    :current-step="stepNumber"
                    :total-steps="totalSteps"
                    :use-exit-modal="!isBriefAccepted && agreed === true"
                    :is-preview="isPreview"
                    @go-next-step="goNextStep"
                    @go-previous-step="goPreviousStep"
                />
            </template>
        </template>
    </page-layout>
</template>

<script>
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import PageLayout from '../../layouts/PageLayout';
    import { get } from 'lodash';
    import FloatOnMask from '../../library/FloatOnMask';
    import StepFooter from './partials/StepFooter';
    import ActivationBriefMixin from './mixins/ActivationBriefMixin';
    import StepError from './partials/StepError';
    import StepHeader from './partials/StepHeader';
    import { getCustomizableText, getCustomizableTitle } from '../../../helpers/activations/default-texts';
    import { CUSTOM_CONTENT_RIGHTS } from '../../../constants/content-rights';
    import { getActivationFiles } from '../../../helpers/activations/activation-files';
    import { CONTENT_AGREEMENT_FILE_TYPE } from '../../../constants/activation-files';

    export default {
        name: 'ActivationBriefAgreementsView',
        components: {
            StepHeader,
            StepError,
            StepFooter,
            FloatOnMask,
            ErrorMessageCard,
            PageLayout
        },
        mixins: [ActivationBriefMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            step: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingBrief: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object,
                default: () => ({})
            },
            errorMessage: {
                type: String,
                default: ''
            },
            usesAlternateTerms: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                agreed: false
            };
        },
        computed: {
            pageTitle() {
                return getCustomizableTitle(this.brief, 'content_rights_title');
            },
            stepOverview() {
                return getCustomizableText(this.brief, 'content_rights_description');
            },
            isDisabled() {
                return !this.agreed;
            },
            stepNumber() {
                return get(this, 'step.meta.stepNumber', 0);
            },
            totalSteps() {
                return get(this, 'step.meta.totalSteps', 0);
            },
            existingContentRights() {
                return get(this.brief, 'content_rights.content_rights.content_rights', false)
                    ? get(this.brief, 'content_rights.content_rights.content_rights', {})
                    : get(this.brief, 'content_rights.content_rights', {});
            },
            agreementText() {
                return get(this.existingContentRights, 'content_rights_header', null);
            },
            standardContentRights() {
                return get(this.existingContentRights, 'standard_content_rights.selected_content_rights', []);
            },
            customContentRights() {
                return get(this.existingContentRights, CUSTOM_CONTENT_RIGHTS, null);
            },
            hasCustomContentRights() {
                return !!this.customContentRights;
            },
            contentRightsFiles() {
                return getActivationFiles(this.brief, CONTENT_AGREEMENT_FILE_TYPE);
            },
            briefAgreed() {
                return !!get(this, 'brief.accepted_at');
            },
            briefName() {
                return this.brief.brand.name;
            }
        },
        methods: {
            goNextStep() {
                this.$emit('go-next-step', this.step, { agreed: this.agreed });
            },
            goPreviousStep() {
                this.$emit('go-previous-step', this.step, { agreed: this.agreed });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .content-rights-list {
        margin-left: $g-spacing-6;
        & li {
            padding-left: $g-spacing-4;
        }
    }

    .agreement-checkbox {
        margin-right: $g-spacing-4;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
